import {
  Badge,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Link,
  Select,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiDownload,
  FiEdit3,
  FiFlag,
  FiRotateCcw,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';

import Button from '../Form/Button';
import { TaskPropsDTO } from '../../dtos';
import { getFormatDate } from '../../utils/date';

type SelectOption = {
  label: string;
  value: string;
};

type QuickUpdatePayload = {
  status?: string;
  priority?: string;
  responsible_id?: string;
};

interface TaskDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskPropsDTO | null;
  loading?: boolean;
  canEdit?: boolean;
  responsibles: SelectOption[];
  actionLoading?: boolean;
  onEdit: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onQuickUpdate: (taskId: string, payload: QuickUpdatePayload) => Promise<void>;
}

const statusConfig = {
  BACKLOG: {
    label: 'Pendente',
    colorScheme: 'yellow',
  },
  FAZENDO: {
    label: 'Em andamento',
    colorScheme: 'blue',
  },
  CONCLUIDO: {
    label: 'Concluída',
    colorScheme: 'green',
  },
};

const priorityConfig = {
  ALTA: {
    label: 'Alta',
    colorScheme: 'red',
  },
  MEDIA: {
    label: 'Média',
    colorScheme: 'orange',
  },
  BAIXA: {
    label: 'Baixa',
    colorScheme: 'gray',
  },
};

function InfoBlock({
  icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <Flex
      align="flex-start"
      gap={3}
      rounded="xl"
      borderWidth="1px"
      borderColor="gray.100"
      bg="gray.50"
      p={4}
    >
      <Flex
        h={10}
        w={10}
        align="center"
        justify="center"
        rounded="full"
        bg="white"
        color="gray.500"
      >
        <Icon as={icon} />
      </Flex>
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          {label}
        </Text>
        <Text mt={1} color="gray.700">
          {value}
        </Text>
      </Box>
    </Flex>
  );
}

export default function TaskDetailDrawer({
  isOpen,
  onClose,
  task,
  loading = false,
  canEdit = false,
  responsibles,
  actionLoading = false,
  onEdit,
  onDelete,
  onQuickUpdate,
}: TaskDetailDrawerProps) {
  const [statusValue, setStatusValue] = useState('');
  const [priorityValue, setPriorityValue] = useState('');
  const [responsibleValue, setResponsibleValue] = useState('');

  useEffect(() => {
    setStatusValue(task?.status || '');
    setPriorityValue(task?.priority || '');
    setResponsibleValue(task?.responsible_id || '');
  }, [task]);

  const hasQuickChanges =
    !!task &&
    (statusValue !== task.status ||
      priorityValue !== task.priority ||
      responsibleValue !== task.responsible_id);

  const handleSaveQuickChanges = async () => {
    if (!task || !hasQuickChanges) {
      return;
    }

    await onQuickUpdate(task.id, {
      status: statusValue !== task.status ? statusValue : undefined,
      priority: priorityValue !== task.priority ? priorityValue : undefined,
      responsible_id:
        responsibleValue !== task.responsible_id ? responsibleValue : undefined,
    });
  };

  const normalizedStatus = task?.status
    ? statusConfig[task.status as keyof typeof statusConfig]
    : null;
  const normalizedPriority = task?.priority
    ? priorityConfig[task.priority as keyof typeof priorityConfig]
    : null;

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" borderColor="gray.100" py={5}>
          <Stack spacing={3}>
            <Text fontSize="xs" fontWeight="bold" letterSpacing="0.12em" color="gray.400">
              DETALHES DA DEMANDA
            </Text>
            <Skeleton isLoaded={!loading}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {task?.title || 'Carregando demanda'}
              </Text>
            </Skeleton>
            <HStack spacing={2} wrap="wrap">
              {normalizedStatus ? (
                <Badge colorScheme={normalizedStatus.colorScheme} rounded="full" px={3} py={1}>
                  {normalizedStatus.label}
                </Badge>
              ) : null}
              {normalizedPriority ? (
                <Badge colorScheme={normalizedPriority.colorScheme} rounded="full" px={3} py={1}>
                  Prioridade {normalizedPriority.label}
                </Badge>
              ) : null}
              {task?.deadline ? (
                <Badge
                  colorScheme="purple"
                  rounded="full"
                  px={3}
                  py={1}
                >
                  Prazo {getFormatDate(new Date(task.deadline), 'dd/MM/yyyy')}
                </Badge>
              ) : null}
            </HStack>
          </Stack>
        </DrawerHeader>

        <DrawerBody py={6}>
          <Stack spacing={6}>
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="semibold" color="gray.500">
                Descrição
              </Text>
              <Skeleton isLoaded={!loading}>
                <Box
                  rounded="2xl"
                  borderWidth="1px"
                  borderColor="gray.100"
                  bg="gray.50"
                  px={5}
                  py={4}
                  color="gray.700"
                  dangerouslySetInnerHTML={{
                    __html: task?.description || '<p>Sem descrição cadastrada.</p>',
                  }}
                />
              </Skeleton>
            </Box>

            <Stack spacing={3}>
              <Skeleton isLoaded={!loading}>
                <InfoBlock
                  icon={FiUser}
                  label="Responsável"
                  value={task?.responsible?.name || 'Não definido'}
                />
              </Skeleton>
              <Skeleton isLoaded={!loading}>
                <InfoBlock
                  icon={FiAlertCircle}
                  label="Apoiador"
                  value={task?.voter?.name || 'Não informado'}
                />
              </Skeleton>
              <Skeleton isLoaded={!loading}>
                <InfoBlock
                  icon={FiCalendar}
                  label="Criada em"
                  value={
                    task?.date
                      ? getFormatDate(new Date(task.date), 'dd/MM/yyyy')
                      : '-'
                  }
                />
              </Skeleton>
              <Skeleton isLoaded={!loading}>
                <InfoBlock
                  icon={FiCheckCircle}
                  label="Concluída em"
                  value={
                    task?.done_at
                      ? getFormatDate(new Date(task.done_at), 'dd/MM/yyyy HH:mm')
                      : 'Ainda não concluída'
                  }
                />
              </Skeleton>
            </Stack>

            {task?.attachment_url ? (
              <Link
                href={task.attachment_url}
                isExternal
                rounded="xl"
                borderWidth="1px"
                borderColor="gray.100"
                bg="gray.50"
                px={4}
                py={3}
                _hover={{ textDecoration: 'none', borderColor: 'gray.200' }}
              >
                <HStack justify="space-between">
                  <HStack spacing={3}>
                    <Icon as={FiDownload} color="gray.500" />
                    <Box>
                      <Text fontWeight="semibold" color="gray.700">
                        Anexo da demanda
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Abrir documento em nova aba
                      </Text>
                    </Box>
                  </HStack>
                  <Text fontSize="sm" color="blue.500">
                    Baixar
                  </Text>
                </HStack>
              </Link>
            ) : null}

            {canEdit && task ? (
              <>
                <Divider />
                <Box>
                  <Text mb={4} fontSize="sm" fontWeight="semibold" color="gray.500">
                    Atualização rápida
                  </Text>
                  <Stack spacing={4}>
                    <Box>
                      <Text mb={2} fontSize="sm" color="gray.600">
                        Status
                      </Text>
                      <Select
                        bg="gray.50"
                        borderColor="gray.200"
                        value={statusValue}
                        onChange={event => setStatusValue(event.target.value)}
                      >
                        <option value="BACKLOG">Pendente</option>
                        <option value="FAZENDO">Em andamento</option>
                        <option value="CONCLUIDO">Concluída</option>
                      </Select>
                    </Box>
                    <Box>
                      <Text mb={2} fontSize="sm" color="gray.600">
                        Prioridade
                      </Text>
                      <Select
                        bg="gray.50"
                        borderColor="gray.200"
                        value={priorityValue}
                        onChange={event => setPriorityValue(event.target.value)}
                      >
                        <option value="ALTA">Alta</option>
                        <option value="MEDIA">Média</option>
                        <option value="BAIXA">Baixa</option>
                      </Select>
                    </Box>
                    <Box>
                      <Text mb={2} fontSize="sm" color="gray.600">
                        Responsável
                      </Text>
                      <Select
                        bg="gray.50"
                        borderColor="gray.200"
                        value={responsibleValue}
                        onChange={event => setResponsibleValue(event.target.value)}
                      >
                        {responsibles.map(responsible => (
                          <option key={responsible.value} value={responsible.value}>
                            {responsible.label}
                          </option>
                        ))}
                      </Select>
                    </Box>
                    <Flex gap={3} wrap="wrap">
                      <Button
                        width="fit-content"
                        leftIcon={<FiCheckCircle />}
                        isLoading={actionLoading}
                        onClick={() =>
                          onQuickUpdate(task.id, { status: 'CONCLUIDO' })
                        }
                      >
                        Concluir
                      </Button>
                      <Button
                        width="fit-content"
                        bgColor="gray.100"
                        color="gray.700"
                        leftIcon={<FiRotateCcw />}
                        isLoading={actionLoading}
                        onClick={() =>
                          onQuickUpdate(task.id, { status: 'BACKLOG' })
                        }
                      >
                        Reabrir
                      </Button>
                      <Button
                        width="fit-content"
                        bgColor="gray.100"
                        color="gray.700"
                        leftIcon={<FiFlag />}
                        isDisabled={!hasQuickChanges}
                        isLoading={actionLoading}
                        onClick={handleSaveQuickChanges}
                      >
                        Salvar ajustes
                      </Button>
                    </Flex>
                  </Stack>
                </Box>
              </>
            ) : null}
          </Stack>
        </DrawerBody>

        {task ? (
          <DrawerFooter borderTopWidth="1px" borderColor="gray.100">
            <Flex w="100%" justify="space-between" gap={3}>
              <Button
                width="fit-content"
                bgColor="gray.100"
                color="gray.700"
                leftIcon={<FiEdit3 />}
                onClick={() => onEdit(task.id)}
              >
                Editar
              </Button>
              {canEdit ? (
                <Button
                  width="fit-content"
                  bgColor="#FEE2E2"
                  color="#B91C1C"
                  leftIcon={<FiTrash2 />}
                  onClick={() => onDelete(task.id)}
                >
                  Excluir
                </Button>
              ) : null}
            </Flex>
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
